if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.bindOnScroll = this.onScroll.bind(this);
      this.boundUpdateBadgeOffset = this.updateBadgeOffset.bind(this);
      this.debounceResize = debounce(this.handleResize.bind(this), 200)
    }

    connectedCallback() {
      this.setVariables();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.bindOnScroll);
      window.removeEventListener('resize', this.debounceResize);
    }

    setVariables() {
      this.medias = Array.from(this.querySelectorAll('.slider__item.media-gallery__item'));
      this.filteredMedias = this.medias.filter(media => media.getBoundingClientRect().width > 0);
      this.variantMedias = this.medias.filter(media => media.classList.contains('.media-gallery__item--variant'))
      this.slider = this.querySelector('component-slider');
      this.scrollThumbnailsContainer = this.querySelector('.media-gallery__thumbnails');
      this.badgesOverlay = this.querySelector('.media-gallery__badges');
      this.header = document.querySelector('.header-wrapper');
      this.counterType = this.dataset.counterType;

      this.setSrollThumbnails();
      this.setGalleryOffset();
      this.patchSlider();
      this.updateBadgeOffset();

      window.addEventListener('scroll', this.bindOnScroll);
      window.addEventListener('resize', this.debounceResize);
    }

    handleResize() {
      this.setGalleryOffset();
      this.updateBadgeOffset();
    }

    patchSlider() {
      if (!this.slider || this.sliderPatched) return;

      this.sliderPatched = true;
      const gallery = this;
      const originalPost = this.slider.postUpdateSliderPosition.bind(this.slider);
      this.slider.postUpdateSliderPosition = function(stopNavigating) {
        originalPost(stopNavigating);
        gallery.updateBadgeOffset();
        if (!gallery.thumbnails?.length) return;
        const idx = Math.max(0, Math.min(gallery.slider.currentIndex, gallery.thumbnails.length - 1));
        gallery.thumbnails.forEach(t => t.classList.remove('active'));
        gallery.setThumbnailActive(gallery.thumbnails[idx]);
      };
    }

    updateBadgeOffset() {
      if (!this.badgesOverlay) return;

      const idx = this.slider ? Math.max(0, Math.min(this.slider.currentIndex, this.filteredMedias.length - 1)) : 0;
      const activeMedia = this.filteredMedias[idx];
      const image = activeMedia ? activeMedia.querySelector('.product-media img') : null;

      if (!image || !image.complete || !image.naturalWidth || !image.naturalHeight) {
        if (image) image.addEventListener('load', this.boundUpdateBadgeOffset, { once: true });
        this.style.setProperty('--media-photo-offset-top', '0px');
        this.style.setProperty('--media-photo-offset-left', '0px');
        return;
      }

      const container = image.closest('.product-media');
      const rect = image.getBoundingClientRect();
      let offsetTop = 0;
      let offsetLeft = 0;

      if (container && container.classList.contains('media-fit-contain') && rect.width > 0 && rect.height > 0) {
        const boxRatio = rect.width / rect.height;
        const naturalRatio = image.naturalWidth / image.naturalHeight;

        if (naturalRatio > boxRatio) {
          const renderedHeight = rect.width / naturalRatio;
          offsetTop = Math.max(0, (rect.height - renderedHeight) / 2);
        } else if (naturalRatio < boxRatio) {
          const renderedWidth = rect.height * naturalRatio;
          offsetLeft = Math.max(0, (rect.width - renderedWidth) / 2);
        }
      }

      this.style.setProperty('--media-photo-offset-top', `${offsetTop}px`);
      this.style.setProperty('--media-photo-offset-left', `${offsetLeft}px`);
    }

    setActiveMedia(id, prepend) {
      const media = this.medias.find(element => element.dataset.mediaId == id);
      if (!media || media.classList.contains('is-active')) return;

      this.medias.forEach(media => {
        media.classList.remove('is-active');
      });
      media.classList.add('is-active');

      const container = this.querySelector('.slider__list');
      if (prepend == true && container) {
        container.insertBefore(media, this.medias[0]);
        requestAnimationFrame(() => {
          this.resetSlider();
          this.setVariables();
        })
      }
    }

    resetSlider() {
      if (!this.slider) return;
      this.slider.removeEventListeners();
      this.slider.setUpSlider();
    }

    setSrollThumbnails() {
      if (!this.scrollThumbnailsContainer) return;

      this.scrollThumbnailsContainer.innerHTML = '';
      const fragment = document.createDocumentFragment();
      this.filteredMedias.forEach((media, index) => {
        const item = document.createElement('div');
        item.classList.add('thumbnail', `thumbnail--${this.counterType.slice(0, -1)}`);
        item.dataset.id = media.dataset.id;
        if (this.counterType === 'numbers') item.textContent = index + 1;
        if (this.counterType === 'images') item.style.setProperty('--thumbnail-image', `url(${media.dataset.sliderThumbnailImage})`)
        if (index === 0) item.classList.add('active');
        if (this.slider) {
          item.addEventListener('click', () => this.slider.navigateToSlide(index, true, 'thumbnail'));
        } else {
          item.addEventListener('click', () => this.scrollIntoMedia(media, item));
        }
        fragment.appendChild(item);
      });
      this.scrollThumbnailsContainer.appendChild(fragment);

      this.thumbnails = Array.from(this.scrollThumbnailsContainer.querySelectorAll('.thumbnail'));
    }

    scrollIntoMedia(media, thumbnail) {
      window.scrollTo({ top: media.offsetTop, behavior: 'smooth' });
      this.setThumbnailActive(thumbnail)
    }

    onScroll() {
      if (!this.thumbnails || this.thumbnails.length <= 0) return;
      this.thumbnails.forEach(t => {
        t.classList.remove('active');
      })
      this.filteredMedias.forEach((media) => {
        if (window.scrollY >= media.offsetTop && window.scrollY <= media.offsetTop + media.getBoundingClientRect().height) {
          this.setThumbnailActive(this.thumbnails.find(thumbnail => thumbnail.dataset.id == media.dataset.id));
        }
      })
    }

    setThumbnailActive(thumbnail) {
      if (!thumbnail) return;

      thumbnail.classList.add('active');

      const containerScrollTop = this.scrollThumbnailsContainer.scrollTop;
      const containerClientHeight = this.scrollThumbnailsContainer.clientHeight;
      const thumbnailOffsetTop = thumbnail.offsetTop;
      const thumbnailHeight = thumbnail.getBoundingClientRect().height;
      const containerGap = parseFloat(computePixelValue(getComputedStyle(this.scrollThumbnailsContainer).rowGap) || computePixelValue(getComputedStyle(this.scrollThumbnailsContainer).gap) || 0);
      const scrollOffsetY = thumbnailHeight + containerGap;
      let newScrollTop = containerScrollTop;

      // Ensure thumbnail is fully visible vertically
      if (thumbnailOffsetTop < containerScrollTop + scrollOffsetY) {
        newScrollTop = thumbnailOffsetTop - scrollOffsetY;
      } else if (thumbnailOffsetTop + thumbnailHeight > containerScrollTop + containerClientHeight - scrollOffsetY) {
        newScrollTop = thumbnailOffsetTop + thumbnailHeight - containerClientHeight + scrollOffsetY;
      }

      this.scrollThumbnailsContainer.scrollTo({ top: newScrollTop, behavior: 'smooth' });
    }

    setGalleryOffset() {
      this.style.setProperty('--gallery-offset-top', `${this.offsetTop}px`);
      this.style.setProperty('--gallery-offset-left', `${this.offsetLeft}px`);
    }
  }

  customElements.define('media-gallery', MediaGallery);
}